/*
 * Copyright (c) 2020-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

import { logClientError } from './clientLog';

/**
 * A fetch result that cannot be mistaken for data.
 *
 * The SPA's recurring defect (code-health survey, 03-silent-failure #15) is
 * `.catch(() => ({ data: { data: [] } }))`: a failed request rendered
 * pixel-identical to an empty account. This type makes that unwritable — the
 * data is only reachable behind an `ok` check, so every caller must decide
 * what a failure looks like, and every failure is logged in one place.
 *
 * `Certificates.tsx` is the in-repo reference for what callers should do with
 * the flag: per-section error props, with `EmptyState` reserved for a
 * fulfilled response that genuinely returned zero rows.
 */
export type Loaded<T> = { ok: true; data: T } | { ok: false; error: unknown };

/**
 * Await a promise into a `Loaded<T>` that never rejects. The failure is
 * recorded via {@link logClientError} under `context` — callers render it,
 * they don't re-log it.
 *
 * ```ts
 * const [courses, groups] = await Promise.all([
 *   load('learn/Dashboard courses', api.get('/courses/me')),
 *   load('learn/Dashboard groups', api.get('/groups/me')),
 * ]);
 * if (!courses.ok) { ...render the error state, not EmptyState... }
 * ```
 */
export async function load<T>(context: string, promise: Promise<T>): Promise<Loaded<T>> {
  try {
    return { ok: true, data: await promise };
  } catch (error) {
    logClientError(context, error);
    return { ok: false, error };
  }
}

/**
 * Explicit, named degradation for values where a failure may legitimately
 * fall back (a KPI tile degrading to 0) instead of blocking a screen. Using
 * this is a visible decision in the diff, unlike a silent `.catch`.
 */
export function loadedOr<T>(result: Loaded<T>, fallback: T): T {
  return result.ok ? result.data : fallback;
}

/**
 * {@link load} for the shape almost every list endpoint in this API returns:
 * `{ data: { data: T[] } }`. Added for the Wave-2.5 sweep, where the same
 * unwrap-and-default-to-empty line appeared at 30+ call sites; keeping it here
 * means "a fulfilled response with no rows" is spelled one way and stays
 * distinguishable from `ok: false`.
 *
 * A fulfilled response with a null/absent envelope is still a success with
 * zero rows — only a rejection is a failure.
 *
 * An envelope that is present but not an array is zero rows too. The
 * per-call-site code this replaced guarded with `Array.isArray(...) ? ... : []`
 * for a reason: callers spread and `.map()` the result, so a 200 carrying an
 * object where the list should be threw "is not iterable" inside an async
 * callback — an unhandled rejection that leaves the screen spinning with no
 * error card. The guard lives here so all 30+ call sites get it.
 */
export function loadList<T>(
  context: string,
  request: Promise<{ data?: { data?: T[] | null } }>
): Promise<Loaded<T[]>> {
  return load(
    context,
    request.then((res) => {
      const rows = res.data?.data;
      return Array.isArray(rows) ? rows : [];
    })
  );
}
